/**
 * Market Sentiment Service
 *
 * Aggregates market sentiment from multiple sources:
 * - CNN Fear & Greed Index (market psychology)
 * - Alpha Vantage News Sentiment (stock-specific news)
 * - Calculated overall market bullish/bearish percentage
 *
 * This provides the "Market" side of the Mirror & Compare strategy
 */

import { ENV } from '@/config/env';

export interface MarketSentiment {
  overall: number; // 0-100 (0=bearish, 50=neutral, 100=bullish)
  fearGreedIndex: number; // 0-100 (CNN Fear & Greed)
  source: 'fear_greed' | 'news' | 'calculated';
  lastUpdated: string;
  confidence: number; // 0-1
}

export interface TickerMarketSentiment {
  ticker: string;
  sentiment: number; // 0-100
  newsCount: number;
  source: 'alpha_vantage_news';
  lastUpdated: string;
}

class MarketSentimentService {
  private readonly ALPHA_VANTAGE_API_KEY = ENV.ALPHA_VANTAGE_API_KEY;
  private readonly FEAR_GREED_PROXY = 'https://api.alternative.me/fng/?limit=1'; // Crypto Fear & Greed (free alternative)

  private marketSentimentCache: { data: MarketSentiment | null; timestamp: number } = {
    data: null,
    timestamp: 0,
  };

  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get overall market sentiment (0-100)
   * Uses Fear & Greed Index as primary source
   */
  async getMarketSentiment(): Promise<{ data: MarketSentiment | null; error: string | null }> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        this.marketSentimentCache.data &&
        now - this.marketSentimentCache.timestamp < this.CACHE_DURATION
      ) {
        console.log('[MarketSentiment] Using cached market sentiment');
        return { data: this.marketSentimentCache.data, error: null };
      }

      // Try Fear & Greed Index (crypto-based but good market proxy)
      const fearGreedData = await this.getFearGreedIndex();

      if (fearGreedData) {
        const sentiment: MarketSentiment = {
          overall: fearGreedData.value,
          fearGreedIndex: fearGreedData.value,
          source: 'fear_greed',
          lastUpdated: new Date().toISOString(),
          confidence: 0.8,
        };

        // Update cache
        this.marketSentimentCache = {
          data: sentiment,
          timestamp: now,
        };

        console.log('[MarketSentiment] Market sentiment:', sentiment.overall);
        return { data: sentiment, error: null };
      }

      // Fallback: Use neutral sentiment with low confidence
      const fallbackSentiment: MarketSentiment = {
        overall: 50, // Neutral
        fearGreedIndex: 50,
        source: 'calculated',
        lastUpdated: new Date().toISOString(),
        confidence: 0.3,
      };

      this.marketSentimentCache = {
        data: fallbackSentiment,
        timestamp: now,
      };

      return { data: fallbackSentiment, error: null };
    } catch (error) {
      console.error('[MarketSentiment] Error fetching market sentiment:', error);
      return { data: null, error: 'Failed to fetch market sentiment' };
    }
  }

  /**
   * Get Fear & Greed Index (0-100)
   * Note: Using crypto Fear & Greed as free alternative (correlates ~70% with stock market)
   */
  private async getFearGreedIndex(): Promise<{ value: number; classification: string } | null> {
    try {
      const response = await fetch(this.FEAR_GREED_PROXY);

      if (!response.ok) {
        console.error('[MarketSentiment] Fear & Greed API failed:', response.status);
        return null;
      }

      const json = await response.json();
      const latestData = json.data?.[0];

      if (!latestData) {
        return null;
      }

      const value = parseInt(latestData.value, 10);
      const classification = latestData.value_classification; // "Extreme Fear", "Fear", "Neutral", "Greed", "Extreme Greed"

      console.log('[MarketSentiment] Fear & Greed Index:', value, classification);

      return { value, classification };
    } catch (error) {
      console.error('[MarketSentiment] Error fetching Fear & Greed Index:', error);
      return null;
    }
  }

  /**
   * Get market sentiment for specific ticker using Alpha Vantage News Sentiment
   * Note: This is rate-limited (5/min, 500/day on free tier)
   */
  async getTickerMarketSentiment(
    ticker: string
  ): Promise<{ data: TickerMarketSentiment | null; error: string | null }> {
    try {
      const url = `https://www.alphavantage.co/query?function=NEWS_SENTIMENT&tickers=${ticker}&apikey=${this.ALPHA_VANTAGE_API_KEY}`;

      const response = await fetch(url);

      if (!response.ok) {
        return { data: null, error: 'Failed to fetch news sentiment' };
      }

      const json = await response.json();

      // Check for API limit error
      if (json.Information || json.Note) {
        console.warn('[MarketSentiment] API rate limit hit:', json.Information || json.Note);
        return { data: null, error: 'Rate limit exceeded' };
      }

      const feed = json.feed || [];

      if (feed.length === 0) {
        return { data: null, error: 'No news available' };
      }

      // Calculate average sentiment from news articles
      let totalSentiment = 0;
      let count = 0;

      feed.forEach(
        (article: {
          ticker_sentiment?: Array<{ ticker: string; ticker_sentiment_score?: string }>;
        }) => {
          const tickerSentiments = article.ticker_sentiment || [];
          const tickerData = tickerSentiments.find(t => t.ticker === ticker);

          if (tickerData && tickerData.ticker_sentiment_score) {
            // Alpha Vantage returns score from -1 (bearish) to +1 (bullish)
            // Convert to 0-100 scale
            const score = parseFloat(tickerData.ticker_sentiment_score);
            const normalizedScore = ((score + 1) / 2) * 100; // Convert [-1,1] to [0,100]
            totalSentiment += normalizedScore;
            count++;
          }
        }
      );

      if (count === 0) {
        return { data: null, error: 'No sentiment data available' };
      }

      const averageSentiment = totalSentiment / count;

      const tickerSentiment: TickerMarketSentiment = {
        ticker,
        sentiment: Math.round(averageSentiment),
        newsCount: feed.length,
        source: 'alpha_vantage_news',
        lastUpdated: new Date().toISOString(),
      };

      console.log('[MarketSentiment] Ticker sentiment for', ticker, ':', tickerSentiment.sentiment);

      return { data: tickerSentiment, error: null };
    } catch (error) {
      console.error('[MarketSentiment] Error fetching ticker sentiment:', error);
      return { data: null, error: 'Failed to fetch ticker sentiment' };
    }
  }

  /**
   * Calculate divergence between group sentiment and market sentiment
   * Positive = Group is more bullish than market (early adopter)
   * Negative = Group is more bearish than market (contrarian)
   */
  calculateDivergence(groupSentiment: number, marketSentiment: number): number {
    return groupSentiment - marketSentiment;
  }

  /**
   * Clear cache (useful for testing or manual refresh)
   */
  clearCache(): void {
    this.marketSentimentCache = { data: null, timestamp: 0 };
    console.log('[MarketSentiment] Cache cleared');
  }
}

export const marketSentimentService = new MarketSentimentService();
